import {
  Box,
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
} from "@mui/material";
import Assets from "../../Component/Common/Assets";
import { useDispatch } from "react-redux";
import useSnackBar from "../../Redux/hooks/useSnackBar";
import { useEffect, useState } from "react";
import usePageLoader from "../../Redux/hooks/usePageLoader";
import { getPoStatusTableAction } from "../../Redux/Actions/DashboardActions";
import ErrorHandler from "../../lib/errorHandler";
import dayjs from "dayjs";

function POStatusTable() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { setSnackBar } = useSnackBar();
  const setFullPageLoader = usePageLoader();

  // --------------------- State ---------------------
  const [list, setList] = useState([]);
  const currentDate = dayjs();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.month());
  const currentYear = currentDate.year();
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // --------------------- Handlers ---------------------
  const getDateRange = () => {
    const startDate = dayjs()
      .year(currentYear)
      .month(selectedMonth)
      .startOf("month");
    const endDate = dayjs()
      .year(currentYear)
      .month(selectedMonth)
      .endOf("month");

    return {
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    };
  };

  const getPoStatusTableData = async () => {
  setFullPageLoader(true);
  const { startDate, endDate } = getDateRange();

  try {
    const res = await dispatch(getPoStatusTableAction({ startDate, endDate }));
    const ok = ErrorHandler(res, setSnackBar);

    if (ok) {
      setList(res.payload.data.response);
    }
  } catch (err) {
    setSnackBar("error", err.message);
  } finally {
    setFullPageLoader(false);
  }
};

  const handleMonthClick = (event) => {
    setMonthMenuAnchor(event.currentTarget);
  };

  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setMonthMenuAnchor(null);
  };

  // --------------------- Effects ---------------------
  useEffect(() => {
    getPoStatusTableData();
  }, [selectedMonth]);

  // --------------------- Render ---------------------
  return (
    <Box
      sx={{
        height: "352px",
        border: `0.5px solid ${theme.palette.bgWhite.secondary}`,
        background: theme.palette.bgWhite.third,
        borderRadius: "20px",
        padding: "20px 20px",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            color: theme.palette.bgBlack.secondary,
          }}
        >
          PO Status
        </Typography>

        {/* Month Selector */}
        <Box
          onClick={handleMonthClick}
          sx={{
            display: "flex",
            padding: "6px 16px",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            borderRadius: "30px",
            background: theme.palette.bgWhite.main,
            cursor: "pointer",
            transition: "all 0.2s ease",
            border: `1px solid ${theme.palette.bgWhite.secondary}`,
            boxShadow:
              "46px 32px 16px 0 rgba(183, 194, 215, 0.00), 29px 21px 14px 0 rgba(183, 194, 215, 0.01), 16px 12px 12px 0 rgba(183, 194, 215, 0.05), 7px 5px 9px 0 rgba(183, 194, 215, 0.09), 2px 1px 5px 0 rgba(183, 194, 215, 0.10)",
          }}
        >
          <Assets absolutePath={true} src="/assets/img/Calendar.svg" />
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: theme.palette.bgDarkGray.grey600,
            }}
          >
            {months[selectedMonth]}
          </Typography>
        </Box>

        <Menu
          anchorEl={monthMenuAnchor}
          open={Boolean(monthMenuAnchor)}
          onClose={() => setMonthMenuAnchor(null)}
        >
          {months.map((month, index) => (
            <MenuItem key={month} onClick={() => handleMonthSelect(index)}>
              {month}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          height: "calc(352px - 130px)",
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.primary.main,
            borderRadius: "2px",
            opacity: 0.3,
          },
          "&::-webkit-scrollbar-thumb:hover": { opacity: 0.6 },
        }}
      >
        <Table
          sx={{
            minWidth: 300,
            "& .MuiTableCell-root": {
              borderBottom: `1px solid ${theme.palette.bgWhite.secondary}`,
              padding: "12px 20px 12px 0",
              "&:first-of-type": { paddingLeft: 0 },
              "&:last-child": { paddingRight: 0 },
            },
          }}
        >
          <TableHead>
            <TableRow>
              {["PO Number", "Vendor Name", "Delivery Date"].map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: theme.palette.primary.richOrange,
                    letterSpacing: "0.5px",
                    borderBottom: `1px solid ${theme.palette.bgWhite.secondary}`,
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:last-child td": { borderBottom: 0 },
                  "&:hover": { backgroundColor: theme.palette.bgWhite.secondary },
                }}
              >
                <TableCell
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: theme.palette.bgBlack.secondary,
                    lineHeight: "20px",
                  }}
                >
                  {row.poNumber}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: theme.palette.bgBlack.secondary,
                    lineHeight: "20px",
                  }}
                >
                  {row.vendorName}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: theme.palette.bgBlack.secondary,
                    lineHeight: "20px",
                  }}
                >
                  {dayjs(row.deliveryDate).format("DD-MM-YYYY")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default POStatusTable;
